import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import axiosInstance from '../../utils/axiosInstance';
import { getImageURL } from '../../utils/api';
import AuthService from '../../services/authService';
import NotificationService from '../../services/notificationService';

const STATUS_META = {
  open: { label: 'Open', badge: 'border border-amber-200 bg-amber-50 text-amber-700', dot: 'bg-amber-400' },
  'in-progress': { label: 'In Progress', badge: 'border border-sky-200 bg-sky-50 text-sky-700', dot: 'bg-sky-400' },
  resolved: { label: 'Resolved', badge: 'border border-emerald-200 bg-emerald-50 text-emerald-700', dot: 'bg-emerald-400' },
  closed: { label: 'Closed', badge: 'border border-slate-300 bg-slate-100 text-slate-600', dot: 'bg-slate-400' },
};

const STATUS_TABS = [
  { value: 'open', label: 'Open' },
  { value: 'in-progress', label: 'In Progress' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'closed', label: 'Closed' },
];

function parseDate(input) {
  if (!input) return null;
  const d = new Date(input);
  return Number.isNaN(d.getTime()) ? null : d;
}

function fmtDateTime(input) {
  const d = parseDate(input);
  if (!d) return '—';
  const date = new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).format(d);
  const time = new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', hour12: true }).format(d);
  return `${date}, ${time}`;
}

function fmtShort(input) {
  const d = parseDate(input);
  if (!d) return '—';
  const today = new Date();
  if (d.toDateString() === today.toDateString()) {
    return new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', hour12: true }).format(d);
  }
  return new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short' }).format(d);
}

function truncate(text, limit) {
  if (!text) return '';
  if (text.length <= limit) return text;
  return `${text.slice(0, limit - 1)}…`;
}

function initialsFromName(name) {
  if (!name) return 'NA';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'NA';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function Avatar({ name, gender, imagePath, size = 48, className = '' }) {
  const initials = initialsFromName(name);
  const isMale = gender && ['male', 'm'].includes(String(gender).toLowerCase());
  const bg = isMale ? '#3b82f6' : '#ec4899';

  if (imagePath) {
    return (
      <img
        src={getImageURL(imagePath)}
        alt={name}
        className={`rounded-full object-cover flex-shrink-0 ${className}`}
        style={{ width: size, height: size }}
      />
    );
  }
  return (
    <div
      className={`rounded-full flex items-center justify-center flex-shrink-0 ${className}`}
      style={{ width: size, height: size, background: bg, color: '#fff', fontSize: size * 0.38, fontWeight: 600 }}
    >
      {initials}
    </div>
  );
}

function formatMessageText(text) {
  if (!text) return '';
  const segments = String(text).split(/(\*[^*]+\*)/g);
  return segments.map((segment, idx) => {
    const isBold = segment.startsWith('*') && segment.endsWith('*');
    const content = isBold ? segment.slice(1, -1) : segment;
    return (
      <React.Fragment key={idx}>
        {content.split('\n').map((line, i) => (
          <React.Fragment key={i}>
            {i > 0 && <br />}
            {isBold ? <strong>{line}</strong> : line}
          </React.Fragment>
        ))}
      </React.Fragment>
    );
  });
}

// Issue Type Management Modal (super admin only)
function IssueTypeModal({ onClose, onSaved }) {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: '', description: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await axiosInstance.get('/contact-admin/admin-issue-types');
      setTypes(resp?.data || []);
    } catch {
      setError('Failed to load issue types');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setError('Name is required'); return; }
    setSaving(true);
    setError('');
    try {
      if (editingId) {
        await axiosInstance.patch(`/contact-admin/issue-types/${editingId}`, form);
      } else {
        await axiosInstance.post('/contact-admin/issue-types', form);
      }
      setForm({ name: '', description: '' });
      setEditingId(null);
      await load();
      if (onSaved) onSaved();
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (type) => {
    setSaving(true);
    try {
      await axiosInstance.patch(`/contact-admin/issue-types/${type.id}`, { is_active: !type.is_active });
      await load();
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-lg rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <h2 className="text-lg font-semibold text-slate-900">Manage Issue Types</h2>
          <button onClick={onClose} className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600">
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="max-h-[60vh] overflow-y-auto p-6 space-y-4">
          {error && (
            <p className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</p>
          )}

          <form onSubmit={handleSubmit} className="space-y-3">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">
                {editingId ? 'Edit Type Name' : 'New Issue Type'}
              </label>
              <input
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Schedule Query"
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Description (optional)</label>
              <input
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                placeholder="Brief description for students"
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
              />
            </div>
            <div className="flex gap-2">
              <button
                type="submit"
                disabled={saving}
                className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-60"
              >
                {saving ? 'Saving…' : editingId ? 'Save Changes' : 'Add Type'}
              </button>
              {editingId && (
                <button
                  type="button"
                  onClick={() => { setEditingId(null); setForm({ name: '', description: '' }); setError(''); }}
                  className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                >
                  Cancel
                </button>
              )}
            </div>
          </form>

          <div className="mt-4 divide-y divide-slate-100 rounded-xl border border-slate-200">
            {loading ? (
              <p className="py-4 text-center text-sm text-slate-400">Loading…</p>
            ) : types.length === 0 ? (
              <p className="py-4 text-center text-sm text-slate-400">No issue types yet</p>
            ) : types.map((type) => (
              <div key={type.id} className={`flex items-start justify-between gap-3 px-4 py-3 ${!type.is_active ? 'opacity-60' : ''}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-slate-900">{type.name}</span>
                    {!type.is_active && (
                      <span className="rounded-full border border-slate-300 bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-500">Inactive</span>
                    )}
                    {type.issue_count > 0 && (
                      <span className="rounded-full bg-indigo-100 px-2 py-0.5 text-[10px] font-semibold text-indigo-700">{type.issue_count} issues</span>
                    )}
                  </div>
                  {type.description && (
                    <p className="mt-0.5 text-xs text-slate-500">{type.description}</p>
                  )}
                </div>
                <div className="flex flex-shrink-0 gap-1">
                  <button
                    onClick={() => { setEditingId(type.id); setForm({ name: type.name, description: type.description || '' }); setError(''); }}
                    className="rounded-lg px-2 py-1 text-xs font-medium text-indigo-600 hover:bg-indigo-50"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleToggleActive(type)}
                    className={`rounded-lg px-2 py-1 text-xs font-medium ${type.is_active ? 'text-slate-500 hover:bg-slate-100' : 'text-emerald-600 hover:bg-emerald-50'}`}
                  >
                    {type.is_active ? 'Disable' : 'Enable'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function StudentIssue() {
  const storedUser = AuthService.getUser();
  const isSuperAdmin = Boolean(storedUser?.superAdmin?.superAdminId);

  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('open');
  const [typeFilter, setTypeFilter] = useState('');
  const [courseFilter, setCourseFilter] = useState('');
  const [coursesForFilter, setCoursesForFilter] = useState([]);
  const [selectedIssueId, setSelectedIssueId] = useState(null);
  const [detailLoaded, setDetailLoaded] = useState(new Set());
  const [messageInput, setMessageInput] = useState('');
  const [sending, setSending] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [showIssueTypeModal, setShowIssueTypeModal] = useState(false);
  const [issueTypesForFilter, setIssueTypesForFilter] = useState([]);
  const [tabCounts, setTabCounts] = useState({});

  const messagesEndRef = useRef(null);
  const filterScrollRef = useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  // Load issue list
  const loadIssues = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (statusFilter && statusFilter !== 'all') params.filter = statusFilter;
      if (courseFilter) params.courseId = courseFilter;
      const resp = await axiosInstance.get('/contact-admin/issues', { params });
      setIssues(resp?.data || []);
      // Clear loaded-detail cache so the selected conversation re-fetches its messages
      setDetailLoaded(new Set());
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load issues');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, courseFilter]);

  useEffect(() => { loadIssues(); }, [loadIssues]);

  // Load per-status counts for all tab badges (lightweight single-row query)
  const loadTabCounts = useCallback(async () => {
    try {
      const resp = await axiosInstance.get('/contact-admin/issues/status-counts');
      setTabCounts(resp?.data || {});
    } catch { /* non-fatal */ }
  }, []);

  useEffect(() => { loadTabCounts(); }, [loadTabCounts]);

  // Load issue types for filter
  useEffect(() => {
    axiosInstance.get('/contact-admin/issue-types')
      .then((r) => setIssueTypesForFilter(r?.data || []))
      .catch(() => {});
  }, []);

  // Load distinct courses that appear in issues
  useEffect(() => {
    axiosInstance.get('/contact-admin/issues/courses')
      .then((r) => setCoursesForFilter(r?.data || []))
      .catch(() => {});
  }, []);

  // Auto-select first issue
  useEffect(() => {
    if (issues.length === 0) { setSelectedIssueId(null); return; }
    if (!selectedIssueId || !issues.some((i) => i.id === selectedIssueId)) {
      setSelectedIssueId(issues[0].id);
    }
  }, [issues, selectedIssueId]);

  // Load detail (messages) when issue is selected for the first time
  useEffect(() => {
    if (!selectedIssueId || detailLoaded.has(selectedIssueId)) return;
    let cancelled = false;

    const load = async () => {
      try {
        const resp = await axiosInstance.get(`/contact-admin/issues/${encodeURIComponent(selectedIssueId)}`);
        if (cancelled || !resp?.data) return;
        const data = resp.data;
        setIssues((prev) =>
          prev.map((i) => i.id === selectedIssueId ? { ...i, ...data, messages: data.messages || [], unreadByAdminCount: 0 } : i)
        );
        setDetailLoaded((prev) => new Set([...prev, selectedIssueId]));
        // Signal sidebar to refresh unread count
        window.dispatchEvent(new CustomEvent('ka-student-issue-read'));
        setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
      } catch (err) {
        if (!cancelled) setError(err?.response?.data?.message || 'Failed to load conversation');
      }
    };
    load();
    return () => { cancelled = true; };
  }, [selectedIssueId, detailLoaded]);

  // Scroll to bottom when messages load
  useEffect(() => {
    setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 80);
  }, [selectedIssueId]);

  const filteredIssues = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return issues.filter((issue) => {
      const matchesType = !typeFilter || String(issue.issueType || '').toLowerCase() === typeFilter.toLowerCase();
      if (!matchesType) return false;
      if (!q) return true;
      return [
        issue.subject, issue.description, issue.studentName,
        issue.registrationNumber, issue.issueType, issue.id,
        issue.studentId, issue.telephone,
        ...(issue.messages || []).map((m) => m.message),
      ].some((f) => (f || '').toLowerCase().includes(q));
    });
  }, [issues, searchQuery, typeFilter]);

  // Keep selection valid after filter changes
  useEffect(() => {
    if (filteredIssues.length === 0) { setSelectedIssueId(null); return; }
    if (!filteredIssues.some((i) => i.id === selectedIssueId)) {
      setSelectedIssueId(filteredIssues[0].id);
    }
  }, [filteredIssues, selectedIssueId]);

  const selectedIssue = useMemo(
    () => issues.find((i) => i.id === selectedIssueId) || null,
    [issues, selectedIssueId]
  );

  const checkScrollButtons = () => {
    if (filterScrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = filterScrollRef.current;
      setShowLeftArrow(scrollLeft > 0);
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 1);
    }
  };

  useEffect(() => {
    checkScrollButtons();
    window.addEventListener('resize', checkScrollButtons);
    return () => window.removeEventListener('resize', checkScrollButtons);
  }, []);

  // Keep a ref to selectedIssueId so the socket callback (stable closure) can read it
  const selectedIssueIdRef = useRef(null);
  useEffect(() => { selectedIssueIdRef.current = selectedIssueId; }, [selectedIssueId]);

  // Socket: join room on mount, receive real-time updates, leave on unmount
  useEffect(() => {
    NotificationService.joinContactAdmin();

    const off = NotificationService.onContactAdminUpdate((payload) => {
      if (!payload?.issue) return;
      const { issue: incoming, messages: incomingMessages, type } = payload;
      const viewingThis = selectedIssueIdRef.current === incoming.id;

      setIssues((prev) => {
        const idx = prev.findIndex((i) => i.id === incoming.id);
        let next;

        if (idx === -1) {
          // New issue not yet in the list — prepend it
          next = [incoming, ...prev];
        } else {
          const updated = {
            ...prev[idx],
            ...incoming,
            messages: incomingMessages !== undefined ? incomingMessages : prev[idx].messages,
            // If we're actively viewing this issue, clear the unread badge immediately
            unreadByAdminCount: viewingThis ? 0 : incoming.unreadByAdminCount,
          };
          next = prev.map((i, i_) => (i_ === idx ? updated : i));
        }

        // Re-sort by latest activity (matches backend ORDER BY)
        return [...next].sort((a, b) => {
          const at = new Date(a.lastMessageAt || a.updatedAt || a.createdAt).getTime();
          const bt = new Date(b.lastMessageAt || b.updatedAt || b.createdAt).getTime();
          return bt - at;
        });
      });

      // If we're viewing this issue and new messages arrived, scroll to bottom + sync sidebar badge
      if (viewingThis && incomingMessages) {
        setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 80);
        window.dispatchEvent(new CustomEvent('ka-student-issue-read'));
      }

      // Refresh tab counts when a new issue is created or a status change shifts an issue between tabs
      if (type === 'new_issue' || type === 'status_changed') {
        loadTabCounts();
      }
    });

    return () => {
      NotificationService.leaveContactAdmin();
      off();
    };
  }, [loadTabCounts]); // loadTabCounts is stable (useCallback []), won't re-fire

  const handleSendMessage = async () => {
    if (!messageInput.trim() || !selectedIssueId || sending) return;
    const msg = messageInput.trim();
    setMessageInput('');
    setSending(true);
    try {
      const resp = await axiosInstance.post(
        `/contact-admin/issues/${encodeURIComponent(selectedIssueId)}/messages`,
        { message: msg }
      );
      if (resp?.data) {
        const data = resp.data;
        setIssues((prev) =>
          prev.map((i) =>
            i.id === selectedIssueId
              ? { ...i, ...data, messages: data.messages || i.messages || [] }
              : i
          )
        );
        setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
      }
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to send message');
      setMessageInput(msg);
    } finally {
      setSending(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    if (!selectedIssueId || updatingStatus) return;
    setUpdatingStatus(true);
    try {
      const resp = await axiosInstance.patch(
        `/contact-admin/issues/${encodeURIComponent(selectedIssueId)}/status`,
        { status: newStatus }
      );
      const updated = resp?.data;
      setIssues((prev) =>
        prev.map((i) =>
          i.id === selectedIssueId ? { ...i, ...(updated || {}), status: newStatus } : i
        )
      );
      loadTabCounts();
      // Sidebar's "Open" badge tracks this same status-counts endpoint.
      window.dispatchEvent(new CustomEvent('ka-student-issue-status-changed'));
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to update status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const totalUnreadByAdmin = useMemo(
    () => issues.reduce((acc, i) => acc + (i.unreadByAdminCount || 0), 0),
    [issues]
  );

  return (
    <DashboardLayout title="Student Issues">
      {showIssueTypeModal && (
        <IssueTypeModal
          onClose={() => setShowIssueTypeModal(false)}
          onSaved={() => {
            axiosInstance.get('/contact-admin/issue-types')
              .then((r) => setIssueTypesForFilter(r?.data || []))
              .catch(() => {});
          }}
        />
      )}

      <div className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">
            Student Issues
            {totalUnreadByAdmin > 0 && (
              <span className="ml-2 inline-flex items-center rounded-full bg-red-100 px-2.5 py-0.5 text-sm font-bold text-red-700">
                {totalUnreadByAdmin} unread
              </span>
            )}
          </h1>
          <p className="text-sm text-slate-500">Manage and respond to student-raised issues.</p>
        </div>
        <div className="flex items-center gap-2">
          {loading && <span className="text-sm text-slate-400">Loading…</span>}
          {error && (
            <span className="max-w-xs truncate rounded-lg bg-red-50 px-3 py-1.5 text-sm text-red-700">{error}</span>
          )}
          {isSuperAdmin && (
            <button
              onClick={() => setShowIssueTypeModal(true)}
              className="flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
              </svg>
              Issue Types
            </button>
          )}
          <button
            onClick={loadIssues}
            className="flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
          <button
            onClick={() => window.open('/admin/student-issue-analysis', '_blank', 'noopener')}
            className="flex items-center gap-2 rounded-lg border border-indigo-300 bg-indigo-50 px-3 py-2 text-sm font-medium text-indigo-700 hover:bg-indigo-100 transition"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            Analysis
          </button>
        </div>
      </div>

      {/* Status bars -- one bar per status (Open/In Progress/Resolved/Closed),
          width relative to the busiest status. Reuses STATUS_META's colors so
          this always matches the tab badges below it. Page is already
          super-admin-only (see RequireSuperAdmin in App.js), so no extra gate needed. */}
      <div className="mb-4 grid grid-cols-2 gap-4 rounded-2xl border border-slate-200 bg-white p-4 sm:grid-cols-4">
        {(() => {
          const maxCount = Math.max(1, ...STATUS_TABS.map((t) => tabCounts[t.value] ?? 0));
          return STATUS_TABS.map((tab) => {
            const meta = STATUS_META[tab.value];
            const count = tabCounts[tab.value] ?? 0;
            const widthPct = Math.max((count / maxCount) * 100, count > 0 ? 4 : 0);
            return (
              <div key={tab.value}>
                <div className="mb-1.5 flex items-baseline justify-between">
                  <span className="flex items-center gap-1.5 text-xs font-semibold text-slate-600">
                    <span className={`h-2 w-2 rounded-full ${meta?.dot || 'bg-slate-400'}`} />
                    {tab.label}
                  </span>
                  <span className="text-lg font-bold text-slate-900">{count}</span>
                </div>
                <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
                  <div
                    className={`h-full rounded-full transition-all ${meta?.dot || 'bg-slate-400'}`}
                    style={{ width: `${widthPct}%` }}
                  />
                </div>
              </div>
            );
          });
        })()}
      </div>

      <div className="flex h-[calc(100vh-9.5rem)] overflow-hidden rounded-2xl border border-slate-200 bg-[#f0f2f5] shadow-sm">
        {/* Left: Issue List */}
        <aside className="flex w-full flex-col border-r border-slate-200 bg-white lg:w-[400px]">
          {/* Search */}
          <div className="flex-shrink-0 border-b border-slate-200 px-3 py-2.5">
            <div className="relative">
              <svg className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M10.5 18a7.5 7.5 0 100-15 7.5 7.5 0 000 15z" />
              </svg>
              <input
                type="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by name, reg. no., ID, phone…"
                className="w-full rounded-lg border-none bg-[#f0f2f5] py-2 pl-10 pr-4 text-sm text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>

          {/* Status tabs */}
          <div className="flex-shrink-0 border-b border-slate-200 px-3 py-2">
            <div className="relative flex items-center">
              {showLeftArrow && (
                <button onClick={() => filterScrollRef.current?.scrollBy({ left: -160, behavior: 'smooth' })}
                  className="absolute left-0 z-10 flex h-7 w-7 items-center justify-center rounded-full bg-white shadow-md">
                  <svg className="h-3.5 w-3.5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
              )}
              <div ref={filterScrollRef} onScroll={checkScrollButtons}
                className="flex gap-1 overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
                {STATUS_TABS.map((tab) => {
                  const meta = STATUS_META[tab.value];
                  const isActive = statusFilter === tab.value;
                  const count = tabCounts[tab.value] ?? 0;
                  return (
                    <button key={tab.value} onClick={() => setStatusFilter(tab.value)}
                      className={`flex-shrink-0 flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition ${
                        isActive ? 'bg-indigo-600 text-white shadow-sm' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}>
                      {tab.label}
                      {count > 0 && (
                        <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold ${
                          isActive ? 'bg-white/25 text-white' : `${meta?.badge || ''}`
                        }`}>
                          {count}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
              {showRightArrow && (
                <button onClick={() => filterScrollRef.current?.scrollBy({ left: 160, behavior: 'smooth' })}
                  className="absolute right-0 z-10 flex h-7 w-7 items-center justify-center rounded-full bg-white shadow-md">
                  <svg className="h-3.5 w-3.5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              )}
            </div>
          </div>

          {/* Type filter */}
          {issueTypesForFilter.length > 0 && (
            <div className="flex-shrink-0 border-b border-slate-200 px-3 py-2">
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs text-slate-700 focus:border-indigo-500 focus:outline-none"
              >
                <option value="">All issue types</option>
                {issueTypesForFilter.map((t) => (
                  <option key={t.id} value={t.name}>{t.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Course filter */}
          {coursesForFilter.length > 0 && (
            <div className="flex-shrink-0 border-b border-slate-200 px-3 py-2">
              <select
                value={courseFilter}
                onChange={(e) => setCourseFilter(e.target.value)}
                className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs text-slate-700 focus:border-indigo-500 focus:outline-none"
              >
                <option value="">All courses</option>
                {coursesForFilter.map((c) => (
                  <option key={c.courseId} value={c.courseId}>
                    {c.shortName || c.fullName}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* List */}
          <div className="flex-1 overflow-y-auto">
            {filteredIssues.length === 0 ? (
              <div className="flex h-full items-center justify-center p-8 text-center">
                <div>
                  <svg className="mx-auto mb-3 h-12 w-12 text-slate-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                  <p className="text-sm text-slate-400">No issues found</p>
                </div>
              </div>
            ) : filteredIssues.map((issue) => {
              const isSelected = issue.id === selectedIssueId;
              const hasUnread = (issue.unreadByAdminCount || 0) > 0;
              const meta = STATUS_META[issue.status];
              const preview = truncate(issue.lastMessage || issue.description, 60);
              const isStudentLastMessage = issue.lastMessageSenderType === 'student';

              return (
                <button
                  key={issue.id}
                  type="button"
                  onClick={() => setSelectedIssueId(issue.id)}
                  className={`group w-full border-b border-slate-100 px-4 py-3 text-left transition hover:bg-slate-50 ${isSelected ? 'bg-indigo-50 border-l-2 border-l-indigo-500' : 'border-l-2 border-l-transparent'}`}
                >
                  <div className="flex items-start gap-3">
                    <Avatar
                      name={issue.studentName || issue.registrationNumber || issue.id}
                      gender={issue.studentGender}
                      imagePath={issue.studentImagePath}
                      size={44}
                    />
                    <div className="min-w-0 flex-1">
                      {/* Row 1: Name + time */}
                      <div className="flex items-baseline justify-between gap-1">
                        <span className={`truncate text-[14px] ${hasUnread ? 'font-semibold text-slate-900' : 'font-medium text-slate-700'}`}>
                          {issue.studentName || issue.registrationNumber || issue.id}
                        </span>
                        <time className={`flex-shrink-0 text-[11px] ${hasUnread ? 'font-semibold text-indigo-600' : 'text-slate-400'}`}>
                          {fmtShort(issue.lastMessageAt || issue.updatedAt || issue.createdAt)}
                        </time>
                      </div>
                      {/* Row 2: Status + type + course badges */}
                      <div className="mt-0.5 flex items-center gap-1.5 flex-wrap">
                        <span className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${meta?.badge || 'bg-slate-100 text-slate-600'}`}>
                          {meta?.label || issue.status}
                        </span>
                        {issue.issueType && (
                          <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-500">
                            {issue.issueType}
                          </span>
                        )}
                        {(issue.courseShortName || issue.courseName) && (
                          <span className="rounded-full bg-violet-50 border border-violet-200 px-1.5 py-0.5 text-[10px] font-medium text-violet-700 truncate max-w-[110px]">
                            {issue.courseShortName || issue.courseName}
                          </span>
                        )}
                      </div>
                      {/* Row 3: Preview + unread count badge (WhatsApp style) */}
                      <div className="mt-0.5 flex items-center justify-between gap-2">
                        <p className={`flex-1 truncate text-xs ${hasUnread ? 'font-medium text-slate-800' : 'text-slate-500'}`}>
                          {!isStudentLastMessage && <span className="text-slate-400">You: </span>}
                          {preview}
                        </p>
                        {hasUnread && (
                          <span className="flex-shrink-0 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-indigo-600 px-1.5 text-[10px] font-bold text-white">
                            {issue.unreadByAdminCount > 99 ? '99+' : issue.unreadByAdminCount}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </aside>

        {/* Right: Conversation */}
        {selectedIssue ? (
          <main className="flex flex-1 flex-col bg-[#efeae2]">
            {/* Header */}
            <header className="flex-shrink-0 border-b border-slate-200 bg-[#f0f2f5] px-4 py-3">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3 min-w-0">
                  <Avatar
                    name={selectedIssue.studentName || selectedIssue.registrationNumber || selectedIssue.id}
                    gender={selectedIssue.studentGender}
                    imagePath={selectedIssue.studentImagePath}
                    size={44}
                  />
                  <div className="min-w-0 flex-1">
                    <h2 className="truncate text-[15px] font-semibold text-slate-900">
                      {selectedIssue.studentName || selectedIssue.registrationNumber || selectedIssue.id}
                    </h2>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-slate-500">
                      {selectedIssue.registrationNumber && (
                        <span className="font-medium">Reg: {selectedIssue.registrationNumber}</span>
                      )}
                      {selectedIssue.telephone && (
                        <span className="flex items-center gap-1 font-medium text-slate-600">
                          <svg className="h-3 w-3 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                          </svg>
                          {selectedIssue.telephone}
                        </span>
                      )}
                      {selectedIssue.issueType && (
                        <span className="rounded-full bg-indigo-100 px-2 py-0.5 text-[11px] font-semibold text-indigo-700">
                          {selectedIssue.issueType}
                        </span>
                      )}
                      {(selectedIssue.courseShortName || selectedIssue.courseName) && (
                        <span className="rounded-full bg-violet-100 px-2 py-0.5 text-[11px] font-semibold text-violet-700">
                          {selectedIssue.courseShortName || selectedIssue.courseName}
                        </span>
                      )}
                      <span className="text-slate-400">{selectedIssue.id}</span>
                    </div>
                    <p className="mt-0.5 text-sm font-medium text-slate-800 truncate">{selectedIssue.subject}</p>
                  </div>
                </div>

                {/* Status control (super admin only) */}
                {isSuperAdmin && (
                  <div className="flex flex-shrink-0 flex-col items-end gap-2">
                    <select
                      value={selectedIssue.status}
                      onChange={(e) => handleStatusChange(e.target.value)}
                      disabled={updatingStatus}
                      className="rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200 disabled:opacity-60"
                    >
                      <option value="open">Open</option>
                      <option value="in-progress">In Progress</option>
                      <option value="resolved">Resolved</option>
                      <option value="closed">Closed</option>
                    </select>
                    {(() => {
                      const meta = STATUS_META[selectedIssue.status];
                      return (
                        <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${meta?.badge || ''}`}>
                          {meta?.label || selectedIssue.status}
                        </span>
                      );
                    })()}
                    {selectedIssue.resolvedAt && (
                      <span className="text-[11px] text-slate-400">
                        Resolved {fmtShort(selectedIssue.resolvedAt)}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </header>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-4 py-4 sm:px-8 lg:px-16">
              <div className="space-y-2">
                {/* Initial description bubble */}
                <div className="flex justify-start gap-2">
                  <Avatar
                    name={selectedIssue.studentName || selectedIssue.registrationNumber || 'S'}
                    gender={selectedIssue.studentGender}
                    imagePath={selectedIssue.studentImagePath}
                    size={32}
                  />
                  <div className="max-w-[70%]">
                    <div className="rounded-lg bg-white px-3 py-2 shadow-sm">
                      <p className="mb-1 text-[11px] font-semibold text-slate-500">
                        {selectedIssue.studentName || 'Student'} · Initial message
                      </p>
                      <p className="whitespace-pre-wrap break-words text-[14px] leading-[20px] text-slate-900">
                        {selectedIssue.description}
                      </p>
                      <p className="mt-1 text-right text-[11px] text-slate-400">{fmtDateTime(selectedIssue.createdAt)}</p>
                    </div>
                  </div>
                </div>

                {/* Thread messages */}
                {(selectedIssue.messages || []).map((msg) => {
                  const isAdmin = msg.sender === 'admin';
                  const senderName = msg.senderName || (isAdmin ? 'Admin' : (selectedIssue.studentName || 'Student'));

                  if (isAdmin) {
                    return (
                      <div key={msg.id} className="flex items-end justify-end gap-2">
                        <div className="flex max-w-[70%] flex-col items-end">
                          <p className="mb-1 px-2 text-[11px] font-semibold text-slate-500">{senderName}</p>
                          <div className="rounded-lg bg-[#d9fdd3] px-3 py-2 shadow-sm">
                            <p className="whitespace-pre-wrap break-words text-[14px] leading-[20px] text-slate-900">
                              {formatMessageText(msg.message)}
                            </p>
                            <div className="mt-1 flex items-center justify-end gap-1">
                              <time className="text-[11px] text-slate-500">{fmtDateTime(msg.timestamp)}</time>
                              <svg className="h-3.5 w-3.5 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" />
                              </svg>
                            </div>
                          </div>
                        </div>
                        <Avatar name={senderName} imagePath={msg.senderImagePath} size={32} />
                      </div>
                    );
                  }

                  return (
                    <div key={msg.id} className="flex items-end justify-start gap-2">
                      <Avatar
                        name={senderName}
                        gender={selectedIssue.studentGender}
                        imagePath={selectedIssue.studentImagePath}
                        size={32}
                      />
                      <div className="max-w-[70%]">
                        <div className="rounded-lg bg-white px-3 py-2 shadow-sm">
                          <p className="whitespace-pre-wrap break-words text-[14px] leading-[20px] text-slate-900">
                            {formatMessageText(msg.message)}
                          </p>
                          <p className="mt-1 text-right text-[11px] text-slate-400">{fmtDateTime(msg.timestamp)}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}

                {selectedIssue.status === 'closed' && (
                  <div className="flex justify-center py-4">
                    <span className="rounded-full bg-slate-200 px-4 py-1.5 text-xs font-medium text-slate-600">
                      This issue is closed · No further replies
                    </span>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>
            </div>

            {/* Reply box */}
            {selectedIssue.status !== 'closed' ? (
              <footer className="flex-shrink-0 border-t border-slate-200 bg-[#f0f2f5] px-4 py-3">
                <div className="flex items-end gap-2">
                  <div className="flex-1">
                    <textarea
                      value={messageInput}
                      onChange={(e) => setMessageInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleSendMessage();
                        }
                      }}
                      placeholder="Type a reply…"
                      rows={1}
                      className="w-full resize-none rounded-xl border-none bg-white px-4 py-2.5 text-[14px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      style={{ maxHeight: '120px' }}
                      onInput={(e) => {
                        e.target.style.height = 'auto';
                        e.target.style.height = `${e.target.scrollHeight}px`;
                      }}
                      disabled={sending}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={handleSendMessage}
                    disabled={!messageInput.trim() || sending}
                    className="rounded-full bg-indigo-600 p-2.5 text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
                    title="Send (Enter)"
                  >
                    <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z" />
                    </svg>
                  </button>
                </div>
              </footer>
            ) : null}
          </main>
        ) : (
          <div className="flex flex-1 items-center justify-center bg-[#f8f9fa]">
            <div className="text-center">
              <svg className="mx-auto mb-4 h-20 w-20 text-slate-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              <h3 className="text-base font-medium text-slate-500">Select a conversation</h3>
              <p className="mt-1 text-sm text-slate-400">Choose a student issue to view and respond</p>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
